import classnames from 'classnames/bind'
import { useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalTextPreset } from '~/@types/text-preset'

import InlineCta from '~/components/UI/InlineCta'
import { ProductStatusTags } from '~/components/UI/ProductCard/types'
import ProductTag from '~/components/UI/ProductTag'

import useProductStatusTagsValues from '~/hooks/useProductStatusTagsValues'

import chunk from '~/utils/chunk'

import { CTA } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

const DEFAULT_MAX_SHOWN_TAGS = 3

export type ProductHeaderTagsProps = {
  className?: string
  productStatusTags?: ProductStatusTags
  sustainabilityTags?: string[]
  customTags?: string[]
}

function ProductHeaderTags({
  className,
  productStatusTags,
  sustainabilityTags,
  customTags,
}: ProductHeaderTagsProps) {
  const [showAll, setShowAll] = useState(false)
  const t = useTranslate()
  const formattedStatusTags = useProductStatusTagsValues(productStatusTags)
  const hasStatusTags =
    formattedStatusTags?.length > 0 || customTags?.length > 0
  const hasSustainabilityTags = sustainabilityTags?.length > 0
  const tagsList =
    [
      ...(formattedStatusTags ?? []),
      ...(customTags ?? []),
      ...(sustainabilityTags ?? []),
    ].filter((item) => item) ?? []
  const chunkedTags = chunk(
    tagsList,
    showAll ? tagsList.length : DEFAULT_MAX_SHOWN_TAGS,
  )?.[0]

  return hasStatusTags || hasSustainabilityTags ? (
    <div className={cx(css.ProductHeaderTags, className)}>
      {(hasStatusTags || hasSustainabilityTags) && (
        <div className={cx(css.hideOnLargeScreen, css.mobileTags)}>
          {hasStatusTags && (
            <div className={cx(css.statusTags, css.column)}>
              {formattedStatusTags?.map((value, index) => (
                <ProductTag key={`status_tag_${index}`} className={cx(css.tag)}>
                  {value}
                </ProductTag>
              ))}
              {customTags?.map((value, index) => (
                <ProductTag
                  key={`custom_status_tag_${index}`}
                  className={cx(css.tag)}>
                  {value}
                </ProductTag>
              ))}
            </div>
          )}
          {hasSustainabilityTags && (
            <div className={cx(css.sustainabilityTags, css.column)}>
              {sustainabilityTags?.map((tag, index) => (
                <ProductTag
                  key={`sustainability_tag_${index}`}
                  className={cx(css.tag)}>
                  {tag}
                </ProductTag>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={cx(css.hideOnSmallScreen, css.desktopTags)}>
        {chunkedTags?.length > 0 &&
          chunkedTags?.map((tag, index) => (
            <ProductTag
              key={`sustainability_tag_${index}`}
              className={cx(css.tag)}>
              {tag}
            </ProductTag>
          ))}
        {!showAll && chunkedTags?.length > DEFAULT_MAX_SHOWN_TAGS && (
          <InlineCta
            className={cx(css.viewMore)}
            textPreset={GlobalTextPreset.Cta12Grotesk}
            onClick={() => setShowAll(true)}>{`${t(
            CTA.VIEW_ALL,
          )} (+)`}</InlineCta>
        )}
      </div>
    </div>
  ) : null
}

ProductHeaderTags.defaultProps = {}

export default ProductHeaderTags
